import type {
  HeadingPathSegment,
  ListInventoryItem,
  PageIntentDiagnosis,
  SectionCoverageRow,
  SerpWinnerExcerpt,
  SupportingPagesV2RowResult
} from '../services/dataforseo';
import type { ActionPlanInternalLinkRow } from '../services/inventory-plan-clusters.service';
import type { DomainTopicUrlRow } from './topic-supporting-pages';
import { formatSupportingPagePath, normalizeUrlForCompare } from './topic-supporting-pages';
import type { OnpageIssueView } from './url-detail-onpage-issues';
import type { PlanV2IssueCardPayload, PlanV2IssueDispositionRecord } from './plan-v2-issues';
import { buildPlanV2IssueKey } from './plan-v2-issues';
import type { FreshnessSerpRelation, SerpPublishedDateRow } from './freshness-serp-compare';
import {
  comparePageFreshnessToSerp,
  FRESHNESS_SIX_MONTHS_MS,
  freshnessDiagnosisLabel,
  pageContentLastModifiedLabel,
  parsePageContentLastModifiedDate,
  serpWinnerPublishedDatesFromResults
} from './freshness-serp-compare';
import { encodeWorkItemDetail, FRESHNESS_INFORMATION_GAIN_NOTE, workItemDetailFor } from './plan-v2-work-item-display';

/** @deprecated v1 flat page picker — use TopicOwnershipCompetitorComposeOption. */
export interface TopicOwnershipGapOption {
  id: string;
  competitorDomain: string;
  competitorUrl: string;
  path: string;
  topicLabels: string[];
  relevanceScore: number;
  selected: boolean;
}

export interface TopicOwnershipComposePageOption {
  id: string;
  competitorDomain: string;
  competitorUrl: string;
  path: string;
  topicLabel: string;
  selected: boolean;
}

export interface TopicOwnershipComposeHeadingMeta {
  id: string;
  competitorUrl: string;
  label: string;
  level: number;
}

export interface TopicOwnershipComposeSectionCompetitor {
  url: string;
  domain: string;
  label: string;
  missingCount: number;
}

export interface TopicOwnershipComposeToolOption {
  id: string;
  competitorUrl: string;
  competitorDomain: string;
  label: string;
  selected: boolean;
}

export interface TopicOwnershipComposeListCompetitor {
  url: string;
  domain: string;
  label: string;
  missingToolCount: number;
}

export interface TopicOwnershipComposeState {
  pageGaps: TopicOwnershipComposePageOption[];
  toolGaps: TopicOwnershipComposeToolOption[];
  headingMetas: TopicOwnershipComposeHeadingMeta[];
  selectedHeadingIds: Set<string>;
  activeCompetitorUrl: string;
  activeListCompetitorUrl: string;
  sectionCompetitors: TopicOwnershipComposeSectionCompetitor[];
  listCompetitors: TopicOwnershipComposeListCompetitor[];
}

function truncate(text: string, max = 140): string {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function winnerExamplesByTerm(
  terms: string[],
  excerpts: SerpWinnerExcerpt[],
  limitPerTerm = 1
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const term of terms) {
    const needle = term.toLowerCase();
    const hits = excerpts
      .filter(
        (e) =>
          (e.highlightTerms || []).some((h) => h.toLowerCase() === needle) ||
          (e.excerpt || '').toLowerCase().includes(needle)
      )
      .slice(0, limitPerTerm)
      .map((e) => `#${e.position} ${e.domain}: “${truncate(e.excerpt, 100)}”`);
    if (hits.length) out[term] = hits;
  }
  return out;
}

export function listTopicOwnershipGapOptions(
  domains: Array<{ domain: string; isYourSite: boolean; rows: DomainTopicUrlRow[] }>
): TopicOwnershipGapOption[] {
  const options: TopicOwnershipGapOption[] = [];
  for (const block of domains) {
    if (block.isYourSite) continue;
    for (const row of block.rows) {
      if (row.similarOnYourSite) continue;
      const path = formatSupportingPagePath(row.url);
      options.push({
        id: `${block.domain}::${row.url}`,
        competitorDomain: block.domain,
        competitorUrl: row.url,
        path,
        topicLabels: row.topics.map((t) => t.topicLabel),
        relevanceScore: row.seoIntentScore,
        selected: true
      });
    }
  }
  return options.sort((a, b) => b.relevanceScore - a.relevanceScore);
}

export function topicOwnershipSectionComposeLabel(sc: SectionCoverageRow): string {
  return (sc.competitorHeadingPath || sc.competitorHeading || 'Section').trim();
}

export function topicOwnershipHeadingComposeId(
  competitorUrl: string,
  sc: SectionCoverageRow,
  segIndex: number
): string {
  const urlKey = normalizeUrlForCompare(competitorUrl);
  const num = sc.sectionNumber ?? 0;
  const end = sc.sectionNumberEnd ?? '';
  return `heading::${urlKey}::${num}::${end}::${segIndex}`;
}

export interface TopicOwnershipHeadingComposeSegment extends Pick<HeadingPathSegment, 'heading' | 'level'> {
  treeDepth?: number;
  displayRole?: string;
}

function topicOwnershipHeadingPathFromMemberPaths(
  sc: SectionCoverageRow,
  leafHeading: string
): string | null {
  const needle = leafHeading.trim().toLowerCase();
  if (!needle || !sc.gapMemberPaths?.length) return null;
  for (const member of sc.gapMemberPaths) {
    const path = member.headingPathSegments || [];
    const leaf = (path[path.length - 1]?.heading || '').trim();
    if (leaf.toLowerCase() !== needle) continue;
    const label = path
      .map((seg) => (seg.heading || '').trim())
      .filter(Boolean)
      .join(' > ');
    if (label) return label;
  }
  return null;
}

/** Breadcrumb for one row in the heading tree (respects sibling `treeDepth`, not array order). */
export function topicOwnershipHeadingComposeLabel(
  segments: TopicOwnershipHeadingComposeSegment[],
  segIndex: number
): string {
  const seg = segments[segIndex];
  if (!seg) return '';
  const heading = (seg.heading || '').trim();
  if (!heading) return '';

  const depth = seg.treeDepth ?? segIndex;
  if (depth <= 0) return heading;

  const chain: string[] = [heading];
  let wantDepth = depth - 1;
  for (let i = segIndex - 1; i >= 0 && wantDepth >= 0; i--) {
    const candidate = segments[i];
    const candidateDepth = candidate.treeDepth ?? i;
    if (candidateDepth !== wantDepth) continue;
    const ancestor = (candidate.heading || '').trim();
    if (ancestor) chain.unshift(ancestor);
    wantDepth--;
  }
  return chain.join(' > ');
}

export function topicOwnershipHeadingComposeLabelForSection(
  sc: SectionCoverageRow,
  segments: TopicOwnershipHeadingComposeSegment[],
  segIndex: number
): string {
  const seg = segments[segIndex];
  if (!seg) return '';
  const role = (seg.displayRole || '').trim();
  if (role === 'gap' || role === 'default') {
    const fromMember = topicOwnershipHeadingPathFromMemberPaths(sc, seg.heading || '');
    if (fromMember) return fromMember;
  }
  if (!sc.isGapGroup && sc.competitorHeadingPathSegments?.length) {
    const pathSegs = sc.competitorHeadingPathSegments;
    const leafIndex = pathSegs.findIndex(
      (pathSeg) => (pathSeg.heading || '').trim().toLowerCase() === (seg.heading || '').trim().toLowerCase()
    );
    if (leafIndex >= 0) {
      return pathSegs
        .slice(0, leafIndex + 1)
        .map((pathSeg) => (pathSeg.heading || '').trim())
        .filter(Boolean)
        .join(' > ');
    }
  }
  return topicOwnershipHeadingComposeLabel(segments, segIndex);
}

function isSectionGapComposeSegment(
  seg: TopicOwnershipHeadingComposeSegment,
  segIndex: number,
  total: number
): boolean {
  const role = (seg.displayRole || '').trim();
  if (role === 'ancestor' || role === 'covered-sibling') return false;
  if (role === 'gap') return true;
  return segIndex === total - 1;
}

function sectionGapsForV2Row(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
  let groups = row.sectionCoverageGroups?.length ? [...row.sectionCoverageGroups] : [];
  groups = groups.filter((sc) => !sc.listicleSuppressed);
  return groups.filter((sc) => !sc.sectionCovered || (sc.absentTerms?.length ?? 0) > 0);
}

function listToolsForV2Row(row: SupportingPagesV2RowResult): ListInventoryItem[] {
  const diff = row.listInventoryDiff;
  if (!diff?.detected) return [];
  return diff.competitorOnly || [];
}

export function topicOwnershipToolComposeId(competitorUrl: string, normalizedLabel: string): string {
  const urlKey = normalizeUrlForCompare(competitorUrl);
  const labelKey = (normalizedLabel || '').trim().toLowerCase();
  return `tool::${urlKey}::${labelKey}`;
}

export function buildTopicOwnershipComposeState(input: {
  domains: Array<{ domain: string; isYourSite: boolean; rows: DomainTopicUrlRow[] }>;
  v2Rows: SupportingPagesV2RowResult[] | undefined | null;
  extractDomain: (url: string) => string;
  displaySegmentsForSection: (sc: SectionCoverageRow) => TopicOwnershipHeadingComposeSegment[];
}): TopicOwnershipComposeState | null {
  const pageGaps: TopicOwnershipComposePageOption[] = [];
  const seenPages = new Set<string>();

  for (const block of input.domains) {
    if (block.isYourSite) continue;
    for (const row of block.rows) {
      if (row.similarOnYourSite) continue;
      const key = normalizeUrlForCompare(row.url);
      if (!key || seenPages.has(key)) continue;
      seenPages.add(key);
      pageGaps.push({
        id: `page::${block.domain}::${row.url}`,
        competitorDomain: block.domain,
        competitorUrl: row.url,
        path: formatSupportingPagePath(row.url),
        topicLabel: row.topics[0]?.topicLabel || 'Supporting page',
        selected: false
      });
    }
  }

  for (const row of input.v2Rows || []) {
    if (!row.v2PageGap) continue;
    const key = normalizeUrlForCompare(row.competitorUrl);
    if (!key || seenPages.has(key)) continue;
    seenPages.add(key);
    const domain = input.extractDomain(row.competitorUrl);
    pageGaps.push({
      id: `page::${domain}::${row.competitorUrl}`,
      competitorDomain: domain,
      competitorUrl: row.competitorUrl,
      path: formatSupportingPagePath(row.competitorUrl),
      topicLabel: 'Whole page gap',
      selected: false
    });
  }

  pageGaps.sort((a, b) => a.competitorDomain.localeCompare(b.competitorDomain));

  const headingMetas: TopicOwnershipComposeHeadingMeta[] = [];
  const sectionCompetitors: TopicOwnershipComposeSectionCompetitor[] = [];

  for (const row of input.v2Rows || []) {
    const gaps = sectionGapsForV2Row(row);
    if (!gaps.length) continue;
    const domain = input.extractDomain(row.competitorUrl);
    const path = formatSupportingPagePath(row.competitorUrl);
    let headingCount = 0;
    for (const sc of gaps) {
      const segments = input.displaySegmentsForSection(sc);
      const gapSegmentCount = segments.filter((seg, index) =>
        isSectionGapComposeSegment(seg, index, segments.length)
      ).length;
      headingCount += gapSegmentCount || segments.length;
      segments.forEach((seg, segIndex) => {
        headingMetas.push({
          id: topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex),
          competitorUrl: row.competitorUrl,
          label: topicOwnershipHeadingComposeLabelForSection(sc, segments, segIndex),
          level: seg.level ?? 0
        });
      });
    }
    sectionCompetitors.push({
      url: row.competitorUrl,
      domain,
      label: `${domain} · ${path}`,
      missingCount: headingCount || gaps.length
    });
  }

  sectionCompetitors.sort((a, b) => b.missingCount - a.missingCount);

  const toolGaps: TopicOwnershipComposeToolOption[] = [];
  const listCompetitors: TopicOwnershipComposeListCompetitor[] = [];

  for (const row of input.v2Rows || []) {
    const tools = listToolsForV2Row(row);
    if (!tools.length) continue;
    const domain = input.extractDomain(row.competitorUrl);
    const path = formatSupportingPagePath(row.competitorUrl);
    for (const tool of tools) {
      const label = (tool.label || '').trim();
      const normalizedLabel = (tool.normalizedLabel || label).trim();
      if (!label) continue;
      toolGaps.push({
        id: topicOwnershipToolComposeId(row.competitorUrl, normalizedLabel),
        competitorUrl: row.competitorUrl,
        competitorDomain: domain,
        label,
        selected: false
      });
    }
    listCompetitors.push({
      url: row.competitorUrl,
      domain,
      label: `${domain} · ${path}`,
      missingToolCount: tools.length
    });
  }

  listCompetitors.sort((a, b) => b.missingToolCount - a.missingToolCount);

  if (!pageGaps.length && !headingMetas.length && !toolGaps.length) return null;

  return {
    pageGaps,
    toolGaps,
    headingMetas,
    selectedHeadingIds: new Set<string>(),
    activeCompetitorUrl: sectionCompetitors[0]?.url || listCompetitors[0]?.url || '',
    activeListCompetitorUrl: listCompetitors[0]?.url || '',
    sectionCompetitors,
    listCompetitors
  };
}

export function topicOwnershipComposeHasSelection(state: TopicOwnershipComposeState | null): boolean {
  if (!state) return false;
  if (state.pageGaps.some((page) => page.selected)) return true;
  if (state.toolGaps.some((tool) => tool.selected)) return true;
  return state.selectedHeadingIds.size > 0;
}

export function composeTopicOwnershipWorkItemFromState(
  base: PlanV2IssueCardPayload,
  state: TopicOwnershipComposeState | null
): PlanV2IssueCardPayload {
  if (!state) {
    return {
      ...base,
      changeSummary: 'Select pages, tools, or sections to add.',
      why: 'No gaps selected.',
      actionDetail: encodeWorkItemDetail({
        bullets: ['Re-open the issue and pick pages, tools, or sections to add.']
      })
    };
  }

  const picks: Array<{
    competitorUrl: string;
    kind: 'page' | 'section' | 'list_tool';
    label: string;
    headingLevel?: number;
  }> = [];

  for (const page of state.pageGaps) {
    if (!page.selected) continue;
    picks.push({
      competitorUrl: page.competitorUrl,
      kind: 'page',
      label: page.path || formatSupportingPagePath(page.competitorUrl)
    });
  }

  for (const tool of state.toolGaps) {
    if (!tool.selected) continue;
    picks.push({
      competitorUrl: tool.competitorUrl,
      kind: 'list_tool',
      label: tool.label
    });
  }

  const headingById = new Map(state.headingMetas.map((meta) => [meta.id, meta]));
  for (const id of state.selectedHeadingIds) {
    const meta = headingById.get(id);
    if (!meta) continue;
    picks.push({
      competitorUrl: meta.competitorUrl,
      kind: 'section',
      label: meta.label,
      headingLevel: meta.level > 0 ? meta.level : undefined
    });
  }

  if (!picks.length) {
    return {
      ...base,
      changeSummary: 'Select pages, tools, or sections to add.',
      why: 'No gaps selected.',
      actionDetail: encodeWorkItemDetail({
        bullets: ['Re-open the issue and pick pages, tools, or sections to add.']
      })
    };
  }

  const pageCount = picks.filter((p) => p.kind === 'page').length;
  const toolCount = picks.filter((p) => p.kind === 'list_tool').length;
  const sectionCount = picks.filter((p) => p.kind === 'section').length;
  const summaryParts: string[] = [];
  if (pageCount) summaryParts.push(`${pageCount} page${pageCount === 1 ? '' : 's'}`);
  if (toolCount) summaryParts.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`);
  if (sectionCount) summaryParts.push(`${sectionCount} section${sectionCount === 1 ? '' : 's'}`);
  const summaryList =
    summaryParts.length <= 1
      ? summaryParts[0] || ''
      : summaryParts.length === 2
        ? summaryParts.join(' and ')
        : `${summaryParts.slice(0, -1).join(', ')}, and ${summaryParts[summaryParts.length - 1]}`;

  return {
    ...base,
    changeSummary: `Add ${summaryList} from competitor coverage.`,
    why: 'Close supporting-page gaps against competitors.',
    actionDetail: encodeWorkItemDetail({
      supportingGaps: picks.slice(0, 24),
      gapPages: state.pageGaps
        .filter((page) => page.selected)
        .slice(0, 10)
        .map((page) => ({
          url: page.competitorUrl,
          missingTopic: page.topicLabel
        }))
    })
  };
}

function sectionCoverageRowsForV2Lookup(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
  const groups = sectionGapsForV2Row(row);
  if (groups.length) return groups;
  return row.sectionCoverage?.length ? [...row.sectionCoverage] : [];
}

export function resolveSectionGapLabelFromCoverage(
  sc: SectionCoverageRow,
  leafHeading: string
): string | null {
  const fromMember = topicOwnershipHeadingPathFromMemberPaths(sc, leafHeading);
  if (fromMember) return fromMember;

  const needle = leafHeading.trim().toLowerCase();
  const pathSegs = sc.competitorHeadingPathSegments || [];
  if (pathSegs.length) {
    const leafIdx = pathSegs.findIndex(
      (seg) => (seg.heading || '').trim().toLowerCase() === needle
    );
    if (leafIdx >= 0) {
      return pathSegs
        .slice(0, leafIdx + 1)
        .map((seg) => (seg.heading || '').trim())
        .filter(Boolean)
        .join(' > ');
    }
  }

  if ((sc.competitorHeading || '').trim().toLowerCase() === needle) {
    const path = (sc.competitorHeadingPath || sc.competitorHeading || '').trim();
    return path || null;
  }
  return null;
}

export function resolveSectionGapLabelFromV2Row(
  v2Row: SupportingPagesV2RowResult,
  leafHeading: string
): string | null {
  for (const sc of sectionCoverageRowsForV2Lookup(v2Row)) {
    const resolved = resolveSectionGapLabelFromCoverage(sc, leafHeading);
    if (resolved) return resolved;
  }
  return null;
}

export function collectFocalUrlsWithSectionGapWorkItems(
  records: Map<string, PlanV2IssueDispositionRecord> | Iterable<PlanV2IssueDispositionRecord>
): string[] {
  const urls = new Set<string>();
  const values = records instanceof Map ? records.values() : records;
  for (const record of values) {
    if (record.queue !== 'work_item') continue;
    const detail = workItemDetailFor(record);
    if (!detail?.supportingGaps?.some((gap) => gap.kind === 'section')) continue;
    const url = record.url.trim();
    if (url) urls.add(url);
  }
  return [...urls];
}

export function repairSupportingSectionWorkItemRecord(
  record: PlanV2IssueDispositionRecord,
  v2ByFocalUrl: Map<string, SupportingPagesV2RowResult[]>
): PlanV2IssueCardPayload | null {
  if (record.queue !== 'work_item') return null;
  const detail = workItemDetailFor(record);
  if (!detail?.supportingGaps?.length) return null;

  const v2Rows = v2ByFocalUrl.get(normalizeUrlForCompare(record.url)) || [];
  if (!v2Rows.length) return null;

  let changed = false;
  const supportingGaps = detail.supportingGaps.map((gap) => {
    if (gap.kind !== 'section') return gap;
    const parts = gap.label
      .split('>')
      .map((part) => part.trim())
      .filter(Boolean);
    const leaf = parts[parts.length - 1] || gap.label.trim();
    const v2Row = v2Rows.find(
      (row) => normalizeUrlForCompare(row.competitorUrl) === normalizeUrlForCompare(gap.competitorUrl)
    );
    if (!v2Row) return gap;
    const resolved = resolveSectionGapLabelFromV2Row(v2Row, leaf);
    if (!resolved || resolved.trim() === gap.label.trim()) return gap;
    changed = true;
    return { ...gap, label: resolved };
  });

  if (!changed) return null;

  const { v: _v, ...rest } = detail;
  return {
    issueKey: record.issueKey,
    urlId: record.urlId,
    url: record.url,
    issueType: record.issueType,
    cardTitle: record.cardTitle,
    changeSummary: record.changeSummary,
    why: record.why,
    actionDetail: encodeWorkItemDetail({ ...rest, supportingGaps }),
    diagnosisLabel: record.diagnosisLabel ?? null,
    priorityScore: record.priorityScore ?? null,
    suggestionKey: record.suggestionKey ?? null
  };
}

export interface FreshnessWorkItemContext {
  pageUpdatedLabel: string;
  serpTypicalDateLabel: string | null;
  diagnosisLabel: string;
  serpRelation: FreshnessSerpRelation;
  /** Last update is at least six months ago (even when ahead of stale SERP dates). */
  pageStillAging: boolean;
}

export function buildFreshnessWorkItemContext(input: {
  lastContentModified: unknown;
  serpResults?: SerpPublishedDateRow[] | null;
  nowMs?: number;
}): FreshnessWorkItemContext {
  const nowMs = input.nowMs ?? Date.now();
  const pageDate = parsePageContentLastModifiedDate(input.lastContentModified);
  const serpDates = serpWinnerPublishedDatesFromResults(input.serpResults);
  const compare = comparePageFreshnessToSerp(pageDate, serpDates, nowMs);
  const pageStillAging = pageDate ? nowMs - pageDate.getTime() >= FRESHNESS_SIX_MONTHS_MS : false;
  return {
    pageUpdatedLabel: pageContentLastModifiedLabel(pageDate, input.lastContentModified),
    serpTypicalDateLabel: serpDates.length >= 2 ? compare.serpTypicalDateLabel : null,
    diagnosisLabel: freshnessDiagnosisLabel(pageDate, serpDates, nowMs),
    serpRelation: compare.relation,
    pageStillAging
  };
}

export function collectFreshnessWorkItemUrls(
  records: Map<string, PlanV2IssueDispositionRecord> | Iterable<PlanV2IssueDispositionRecord>
): Array<{ urlId: string; url: string }> {
  const seen = new Set<string>();
  const out: Array<{ urlId: string; url: string }> = [];
  const values = records instanceof Map ? records.values() : records;
  for (const record of values) {
    if (record.queue !== 'work_item' || record.issueType !== 'freshness') continue;
    const urlId = record.urlId.trim();
    const url = record.url.trim();
    if (!urlId || !url || seen.has(urlId)) continue;
    seen.add(urlId);
    out.push({ urlId, url });
  }
  return out;
}

export function repairFreshnessWorkItemRecord(
  record: PlanV2IssueDispositionRecord,
  contextByUrlId: Map<string, FreshnessWorkItemContext>
): PlanV2IssueCardPayload | null {
  if (record.queue !== 'work_item' || record.issueType !== 'freshness') return null;
  const ctx = contextByUrlId.get(record.urlId.trim());
  if (!ctx) return null;

  const base: PlanV2IssueCardPayload = {
    issueKey: record.issueKey,
    urlId: record.urlId,
    url: record.url,
    issueType: record.issueType,
    cardTitle: record.cardTitle,
    changeSummary: record.changeSummary,
    why: record.why,
    actionDetail: record.actionDetail,
    diagnosisLabel: record.diagnosisLabel ?? null,
    priorityScore: record.priorityScore ?? null,
    suggestionKey: record.suggestionKey ?? null
  };
  const refreshed = composeFreshnessWorkItem(base, ctx);
  if (
    (refreshed.actionDetail || '') === (record.actionDetail || '') &&
    (refreshed.why || '') === (record.why || '') &&
    (refreshed.changeSummary || '') === (record.changeSummary || '') &&
    (refreshed.diagnosisLabel || null) === (record.diagnosisLabel ?? null)
  ) {
    return null;
  }
  return refreshed;
}

export function composeFreshnessWorkItem(
  base: PlanV2IssueCardPayload,
  ctx: FreshnessWorkItemContext
): PlanV2IssueCardPayload {
  const paragraphs: string[] = [];
  const aging =
    ctx.pageStillAging ||
    ctx.diagnosisLabel === 'Aging' ||
    ctx.diagnosisLabel === 'Stale' ||
    ctx.diagnosisLabel === 'Behind SERP';
  const stillAgingNote =
    'However, it is still aging, so it is worth updating your page with fresh information if available.';
  const pageDate = ctx.pageUpdatedLabel && ctx.pageUpdatedLabel !== 'No last-update date on crawl'
    ? ctx.pageUpdatedLabel
    : null;

  if (pageDate && ctx.serpTypicalDateLabel && ctx.serpRelation === 'similar') {
    paragraphs.push(
      `Your page was last updated on ${pageDate}, which is similar to other pages in the SERP (${ctx.serpTypicalDateLabel}).`
    );
    if (ctx.pageStillAging) {
      paragraphs.push(stillAgingNote);
      paragraphs.push(FRESHNESS_INFORMATION_GAIN_NOTE);
    }
  } else if (pageDate && ctx.serpTypicalDateLabel && ctx.serpRelation === 'older') {
    paragraphs.push(
      `Your page was last updated on ${pageDate}, which is older than typical top results in the SERP (${ctx.serpTypicalDateLabel}).`
    );
    if (aging) {
      paragraphs.push('Consider refreshing this page with newer information if available.');
      paragraphs.push(FRESHNESS_INFORMATION_GAIN_NOTE);
    }
  } else if (pageDate && ctx.serpTypicalDateLabel && ctx.serpRelation === 'newer') {
    paragraphs.push(
      `Your page was last updated on ${pageDate}, which is newer than typical top results in the SERP (${ctx.serpTypicalDateLabel}).`
    );
    if (ctx.pageStillAging) {
      paragraphs.push(stillAgingNote);
      paragraphs.push(FRESHNESS_INFORMATION_GAIN_NOTE);
    }
  } else if (pageDate && ctx.serpTypicalDateLabel) {
    paragraphs.push(
      `Your page was last updated on ${pageDate}. Typical top results: ${ctx.serpTypicalDateLabel}.`
    );
    if (aging) {
      paragraphs.push('Consider refreshing this page with newer information if available.');
      paragraphs.push(FRESHNESS_INFORMATION_GAIN_NOTE);
    }
  } else if (pageDate) {
    paragraphs.push(`Your page was last updated on ${pageDate}.`);
    if (aging) {
      paragraphs.push('Consider refreshing this page with newer information if available.');
      paragraphs.push(FRESHNESS_INFORMATION_GAIN_NOTE);
    }
  } else {
    paragraphs.push('Review whether this page needs a content refresh.');
  }

  const why = paragraphs[0] || 'Review whether this page needs a content refresh.';

  return {
    ...base,
    changeSummary: aging ? 'Refresh if you have newer information to publish.' : 'Monitor freshness.',
    why,
    actionDetail: encodeWorkItemDetail({ freshness: { paragraphs } })
  };
}

const SERP_INTENT_FAMILY_BOLD: Record<string, { bold: string; suffix: string }> = {
  versus: { bold: 'versus', suffix: ' / comparison' },
  definition: { bold: 'definition', suffix: '' },
  how_to: { bold: 'how-to', suffix: '' },
  listicle: { bold: 'list', suffix: ' / comparison roundup' },
  template: { bold: 'template', suffix: ' / download' },
  pricing: { bold: 'pricing', suffix: '' }
};

function serpIntentFamilyBoldParts(family: string): { bold: string; suffix: string } {
  return SERP_INTENT_FAMILY_BOLD[family] || { bold: family.replace(/_/g, '-'), suffix: '' };
}

function isBuriedIntentPlacementBullet(text: string): boolean {
  return /not the opening/i.test(text);
}

export function composeSerpIntentWorkItem(
  base: PlanV2IssueCardPayload,
  diagnosis: PageIntentDiagnosis | null
): PlanV2IssueCardPayload {
  if (!diagnosis) {
    return base;
  }

  const placementBullet = diagnosis.bullets.find((b) => b.kind === 'placement');
  const best = diagnosis.yourBestSection;
  const serpFamily = diagnosis.serpFamily;
  if (
    best?.label &&
    best?.text &&
    placementBullet &&
    isBuriedIntentPlacementBullet(placementBullet.text) &&
    serpFamily &&
    serpFamily !== 'mixed' &&
    serpFamily !== 'unknown'
  ) {
    const { bold, suffix } = serpIntentFamilyBoldParts(serpFamily);
    const sectionLabel = best.label;
    const passage = truncate(best.text, 100);
    const why = `Strongest ${bold}${suffix} match in your content is lower down the page, under "${sectionLabel}". Consider moving that answer up.`;
    return {
      ...base,
      changeSummary: 'Align page format and angle with dominant SERP intent.',
      why,
      actionDetail: encodeWorkItemDetail({
        intentPlacement: {
          familyBold: bold,
          familySuffix: suffix,
          sectionLabel,
          passage
        }
      })
    };
  }

  const familyParts = serpIntentFamilyBoldParts(serpFamily);
  const serpLabel = serpFamily === 'mixed' ? 'mixed intent' : `${familyParts.bold}${familyParts.suffix}`;
  const lines: string[] = [];
  if (diagnosis.intentAlignedWithSerp) {
    lines.push(`SERP and your page both read as ${serpLabel}.`);
  } else if (diagnosis.serpFamily !== 'unknown') {
    lines.push(`SERP mostly ${serpLabel}.`);
  }
  const extra = diagnosis.bullets.find(
    (b) =>
      b.kind === 'placement' ||
      b.kind === 'lexical_gap' ||
      b.kind === 'lexical_misuse' ||
      b.kind === 'vocabulary_fragmented'
  );
  if (extra && lines.length < 2) {
    lines.push(truncate(extra.text, 140));
  }
  const why = lines.length ? lines.join(' ') : base.why;
  return {
    ...base,
    why,
    actionDetail: extra ? encodeWorkItemDetail({ bullets: lines.slice(0, 2) }) : base.actionDetail
  };
}

export function composeSerpPhraseWorkItem(
  base: PlanV2IssueCardPayload,
  diagnosis: PageIntentDiagnosis | null,
  _targetQuery: string
): PlanV2IssueCardPayload {
  if (!diagnosis) {
    return base;
  }

  const missing = (diagnosis.missingTerms || []).slice(0, 8);
  const misaligned = (diagnosis.misalignedLexicalTerms || []).slice(0, 8);
  const excerpts = diagnosis.serpWinnerExcerpts || [];
  const best = diagnosis.yourBestSection;
  const placement =
    best?.label && best.text ? `${best.label} — “${truncate(best.text, 120)}”` : undefined;

  const exampleTerms = [...missing, ...misaligned].slice(0, 6);
  const examples = winnerExamplesByTerm(exampleTerms, excerpts, 1);

  const parts: string[] = [];
  if (missing.length) parts.push(`${missing.length} missing`);
  if (misaligned.length) parts.push(`${misaligned.length} wrong context`);

  return {
    ...base,
    changeSummary: parts.length ? parts.join(', ') : 'Align vocabulary with winners.',
    why: 'Winners use different phrases than your page.',
    actionDetail: encodeWorkItemDetail({
      missing,
      misaligned,
      examples,
      placement
    })
  };
}

export interface TechnicalDetailComposeOption {
  id: string;
  text: string;
  /** Script/Stylesheet/Duplicate/Tag lines can be picked individually. */
  selectable: boolean;
  selected: boolean;
}

export interface TechnicalIssueComposeOption {
  id: string;
  index: number;
  title: string;
  detailOptions: TechnicalDetailComposeOption[];
  /** Whole-issue toggle when there are no selectable sub-lines. */
  selected: boolean;
}

/** Lines the user can include or exclude independently in compose. */
export function isSelectableTechnicalDetailLine(text: string): boolean {
  return /^(Script|Stylesheet|Duplicate|Tag):/i.test((text || '').trim());
}

export function technicalIssueHasSelectableDetails(opt: TechnicalIssueComposeOption): boolean {
  return opt.detailOptions.some((d) => d.selectable);
}

export function isTechnicalIssueIncluded(opt: TechnicalIssueComposeOption): boolean {
  const selectable = opt.detailOptions.filter((d) => d.selectable);
  if (!selectable.length) return opt.selected;
  return selectable.some((d) => d.selected);
}

export function resolvedTechnicalIssueDetails(opt: TechnicalIssueComposeOption): string[] {
  const meta = opt.detailOptions.filter((d) => !d.selectable).map((d) => d.text);
  const selectable = opt.detailOptions.filter((d) => d.selectable);
  if (!selectable.length) {
    return opt.selected ? meta : [];
  }
  const picked = selectable.filter((d) => d.selected).map((d) => d.text);
  if (!picked.length) return [];
  return [...picked, ...meta];
}

export function technicalParentChecked(opt: TechnicalIssueComposeOption): boolean {
  const selectable = opt.detailOptions.filter((d) => d.selectable);
  if (!selectable.length) return opt.selected;
  return selectable.length > 0 && selectable.every((d) => d.selected);
}

export function setTechnicalIssueParentSelected(
  opt: TechnicalIssueComposeOption,
  checked: boolean
): void {
  const selectable = opt.detailOptions.filter((d) => d.selectable);
  if (selectable.length) {
    for (const d of selectable) {
      d.selected = checked;
    }
  }
  opt.selected = checked;
}

export function listTechnicalIssueComposeOptions(
  issues: OnpageIssueView[],
  kind: 'critical' | 'warning'
): TechnicalIssueComposeOption[] {
  return issues.map((issue, index) => {
    const lines = (issue.details || []).filter(Boolean);
    const detailOptions = lines.map((text, detailIndex) => ({
      id: `${kind}:${index}:${detailIndex}`,
      text,
      selectable: isSelectableTechnicalDetailLine(text),
      selected: true
    }));
    return {
      id: `${kind}:${index}`,
      index,
      title: issue.title,
      detailOptions,
      selected: true
    };
  });
}

export function composeTechnicalIssuesWorkItem(
  base: PlanV2IssueCardPayload,
  options: TechnicalIssueComposeOption[],
  severity: 'critical' | 'warning'
): PlanV2IssueCardPayload {
  const included = options.filter(isTechnicalIssueIncluded);
  if (!included.length) {
    return {
      ...base,
      changeSummary: 'Select issues to include.',
      why: 'No issues selected.',
      actionDetail: encodeWorkItemDetail({ bullets: ['Re-open the issue and pick items to fix.'] })
    };
  }
  const items = included.map((opt) => ({
    title: opt.title,
    details: resolvedTechnicalIssueDetails(opt)
  }));
  const titles = included.map((opt) => opt.title);
  return {
    ...base,
    cardTitle: severity === 'critical' ? 'Technical issues' : 'Technical warnings',
    changeSummary: `Fix ${included.length} on-page issue${included.length === 1 ? '' : 's'}.`,
    why: titles.slice(0, 4).join(' · ') + (titles.length > 4 ? ' …' : ''),
    actionDetail: encodeWorkItemDetail({ technicalItems: items })
  };
}

export interface PlacementSentenceParts {
  before: string;
  anchor: string;
  after: string;
}

/** Split a source sentence so the suggested anchor can render as an inline link. */
export function splitPlacementByAnchor(sentence: string, anchor: string): PlacementSentenceParts | null {
  const text = (sentence || '').trim();
  const needle = (anchor || '').trim();
  if (!text || !needle) return null;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return null;
  return {
    before: text.slice(0, idx),
    anchor: text.slice(idx, idx + needle.length),
    after: text.slice(idx + needle.length)
  };
}

export type InternalLinkDirection = 'to' | 'from';

export function formatInternalLinkRelevanceScore(score: number): string {
  if (!Number.isFinite(score)) return '—';
  const rounded = Math.round(score * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

export function internalLinkDirectionLabel(direction: InternalLinkDirection): string {
  return direction === 'to' ? 'LINK TO' : 'LINK FROM';
}

export function internalLinkDirectionChipClass(direction: InternalLinkDirection): string {
  return direction === 'to' ? 'internal-link-direction-chip--to' : 'internal-link-direction-chip--from';
}

export interface InternalLinkRowDisplay {
  direction: InternalLinkDirection;
  pagePath: string;
  pageUrl: string;
  linkHref: string;
  score: number;
}

/** Issues / work items: only incoming links (edit on other pages pointing at this URL). */
export function isInternalLinkIncomingToPage(
  row: ActionPlanInternalLinkRow,
  pageUrlId: string
): boolean {
  const id = (pageUrlId || '').trim();
  return row.status === 'open' && !!id && row.targetUrlId === id;
}

export function filterInternalLinksIncomingToPage(
  rows: ActionPlanInternalLinkRow[],
  pageUrlId: string
): ActionPlanInternalLinkRow[] {
  const id = (pageUrlId || '').trim();
  if (!id) return [];
  return rows.filter((row) => isInternalLinkIncomingToPage(row, id));
}

export function internalLinkWorkItemIssueKey(row: ActionPlanInternalLinkRow): string {
  return buildPlanV2IssueKey(row.sourceUrlId, 'internal_link', row.suggestionKey);
}

export function internalLinkSuggestionKeyFromIssueKey(issueKey: string): string {
  const parts = (issueKey || '').split(':');
  if (parts.length < 3 || parts[1] !== 'internal_link') return '';
  return parts.slice(2).join(':').trim();
}

export function internalLinkSuggestionKeysInWorkItems(
  records: Map<string, PlanV2IssueDispositionRecord> | Iterable<PlanV2IssueDispositionRecord>
): Set<string> {
  const keys = new Set<string>();
  const values = records instanceof Map ? records.values() : records;
  for (const record of values) {
    if (record.issueType !== 'internal_link' || record.queue !== 'work_item') continue;
    const suffix =
      internalLinkSuggestionKeyFromIssueKey(record.issueKey) || (record.suggestionKey || '').trim();
    if (suffix) keys.add(suffix);
  }
  return keys;
}

/** Incoming links still triaged on the beneficiary Issues card (not already in Work items). */
export function filterInternalLinksPendingIssuesTriage(
  rows: ActionPlanInternalLinkRow[],
  pageUrlId: string,
  inWorkItems: Set<string>
): ActionPlanInternalLinkRow[] {
  return filterInternalLinksIncomingToPage(rows, pageUrlId).filter((row) => !inWorkItems.has(row.suggestionKey));
}

export function buildInternalLinkRowDisplay(
  row: ActionPlanInternalLinkRow,
  pageUrlId: string
): InternalLinkRowDisplay {
  const onThisPage = row.sourceUrlId === pageUrlId;
  return {
    direction: onThisPage ? 'to' : 'from',
    pagePath: formatSupportingPagePath(onThisPage ? row.targetUrl : row.sourceUrl),
    pageUrl: onThisPage ? row.targetUrl : row.sourceUrl,
    linkHref: row.targetUrl,
    score: row.score
  };
}

export interface InternalLinkComposeOption extends InternalLinkRowDisplay {
  id: string;
  row: ActionPlanInternalLinkRow;
  /** Source sentence for inline “where” example */
  placement: string;
  placementParts: PlacementSentenceParts | null;
  selected: boolean;
}

export function listInternalLinkComposeOptions(
  rows: ActionPlanInternalLinkRow[],
  pageUrlId: string,
  inWorkItems?: Set<string>
): InternalLinkComposeOption[] {
  const pending = inWorkItems
    ? filterInternalLinksPendingIssuesTriage(rows, pageUrlId, inWorkItems)
    : filterInternalLinksIncomingToPage(rows, pageUrlId);
  return pending.map((row) => {
      const display = buildInternalLinkRowDisplay(row, pageUrlId);
      const placement = (row.where?.sentence || '').trim();
      const anchorText = (row.suggestedAnchor || '').trim();
      return {
        id: row.suggestionKey,
        row,
        ...display,
        placement,
        placementParts: splitPlacementByAnchor(placement, anchorText),
        selected: true
      };
    });
}

/** One CMS task on the page where copy is edited (source URL). */
export function composeInternalLinkWorkItemOnSourcePage(
  row: ActionPlanInternalLinkRow,
  priorityScore?: number | null
): PlanV2IssueCardPayload {
  const anchor = (row.suggestedAnchor || '').trim() || 'anchor text';
  const sentence = (row.where?.sentence || '').trim();
  const targetPath = formatSupportingPagePath(row.targetUrl);
  const placementParts = splitPlacementByAnchor(sentence, anchor);

  return {
    issueKey: internalLinkWorkItemIssueKey(row),
    urlId: row.sourceUrlId,
    url: row.sourceUrl,
    issueType: 'internal_link',
    suggestionKey: row.suggestionKey,
    cardTitle: 'Internal link',
    changeSummary: `Link to ${targetPath} (“${truncate(anchor, 36)}”).`,
    why: 'Add a contextual link in existing copy.',
    priorityScore: priorityScore ?? null,
    actionDetail: encodeWorkItemDetail({
      internalLinkItems: [
        {
          direction: 'to',
          pagePath: targetPath,
          pageUrl: row.targetUrl,
          linkHref: row.targetUrl,
          anchor,
          score: row.score,
          sentence: sentence || undefined,
          placementParts
        }
      ]
    })
  };
}

export function composeInternalLinkWorkItemsFromSelection(
  base: PlanV2IssueCardPayload,
  selected: InternalLinkComposeOption[]
): PlanV2IssueCardPayload[] {
  const picked = selected.filter((opt) => opt.selected);
  if (!picked.length) {
    return [
      {
        ...base,
        changeSummary: 'Select links to add.',
        why: 'No internal links selected.',
        actionDetail: encodeWorkItemDetail({ bullets: ['Re-open the issue and pick links to include.'] })
      }
    ];
  }
  return picked.map((opt) => composeInternalLinkWorkItemOnSourcePage(opt.row, base.priorityScore));
}

/** @deprecated Prefer {@link composeInternalLinkWorkItemsFromSelection}. */
export function composeInternalLinksWorkItem(
  base: PlanV2IssueCardPayload,
  selected: InternalLinkComposeOption[],
  _pageUrlId: string
): PlanV2IssueCardPayload {
  const items = composeInternalLinkWorkItemsFromSelection(base, selected);
  if (items.length === 1) return items[0];
  const count = items.length;
  return {
    ...items[0],
    changeSummary: `Add ${count} internal links on ${count} source pages.`,
    why: 'Each selected link becomes its own task on the page where you edit copy.',
    actionDetail: encodeWorkItemDetail({
      bullets: items.map((item) => item.changeSummary).slice(0, 12)
    })
  };
}

export function composeInternalLinkWorkItem(
  base: PlanV2IssueCardPayload,
  row: ActionPlanInternalLinkRow,
  _pageUrlId: string
): PlanV2IssueCardPayload {
  return composeInternalLinkWorkItemOnSourcePage(row, base.priorityScore);
}

function internalLinkPairKey(sourceUrl: string, targetUrl: string, anchor: string): string {
  return `${normalizeUrlForCompare(sourceUrl)}::${normalizeUrlForCompare(targetUrl)}::${(anchor || '').trim().toLowerCase()}`;
}

export function buildInternalLinkRowLookup(rows: ActionPlanInternalLinkRow[]): {
  bySuggestionKey: Map<string, ActionPlanInternalLinkRow>;
  byPair: Map<string, ActionPlanInternalLinkRow>;
} {
  const bySuggestionKey = new Map<string, ActionPlanInternalLinkRow>();
  const byPair = new Map<string, ActionPlanInternalLinkRow>();
  for (const row of rows) {
    bySuggestionKey.set(row.suggestionKey, row);
    byPair.set(
      internalLinkPairKey(row.sourceUrl, row.targetUrl, row.suggestedAnchor || ''),
      row
    );
  }
  return { bySuggestionKey, byPair };
}

function parseInternalLinkIssueKeySuffix(issueKey: string): string {
  return internalLinkSuggestionKeyFromIssueKey(issueKey);
}

function resolveInternalLinkRowForRepair(
  record: PlanV2IssueDispositionRecord,
  item: NonNullable<NonNullable<ReturnType<typeof workItemDetailFor>>['internalLinkItems']>[number],
  lookup: ReturnType<typeof buildInternalLinkRowLookup>
): ActionPlanInternalLinkRow | null {
  const suffix = parseInternalLinkIssueKeySuffix(record.issueKey);
  if (suffix && lookup.bySuggestionKey.has(suffix)) {
    return lookup.bySuggestionKey.get(suffix)!;
  }
  const suggestionKey = (record.suggestionKey || '').trim();
  if (suggestionKey && lookup.bySuggestionKey.has(suggestionKey)) {
    return lookup.bySuggestionKey.get(suggestionKey)!;
  }
  const sourceUrl = item.direction === 'from' ? item.pageUrl : record.url;
  const targetUrl = item.linkHref || record.url;
  const pairKey = internalLinkPairKey(sourceUrl, targetUrl, item.anchor);
  return lookup.byPair.get(pairKey) || null;
}

/** Stored work items grouped on the beneficiary URL should live on source pages instead. */
export function needsInternalLinkWorkItemRepair(record: PlanV2IssueDispositionRecord): boolean {
  if (record.issueType !== 'internal_link' || record.queue !== 'work_item') return false;
  const detail = workItemDetailFor(record);
  const items = detail?.internalLinkItems;
  if (!items?.length) return false;
  if (items.length > 1) return true;
  const item = items[0];
  if (item.direction === 'from') return true;
  const keyUrlId = (record.issueKey.split(':')[0] || '').trim();
  if (keyUrlId && keyUrlId !== record.urlId.trim()) return true;
  return false;
}

export function splitInternalLinkWorkItemForRepair(
  record: PlanV2IssueDispositionRecord,
  lookup: ReturnType<typeof buildInternalLinkRowLookup>
): { deleteIssueKey: string; replacements: PlanV2IssueCardPayload[] } | null {
  if (!needsInternalLinkWorkItemRepair(record)) return null;
  const detail = workItemDetailFor(record);
  const items = detail?.internalLinkItems ?? [];
  const replacements: PlanV2IssueCardPayload[] = [];

  for (const item of items) {
    const row = resolveInternalLinkRowForRepair(record, item, lookup);
    if (row) {
      replacements.push(composeInternalLinkWorkItemOnSourcePage(row, record.priorityScore));
      continue;
    }

    const targetUrl = (item.linkHref || record.url).trim();
    const anchor = (item.anchor || '').trim() || 'anchor text';
    const targetPath = formatSupportingPagePath(targetUrl);
    const sourceUrl =
      item.direction === 'from' ? (item.pageUrl || '').trim() : (record.url || '').trim();
    const sourceUrlId = (record.urlId || '').trim();
    const keyUrlId = (record.issueKey.split(':')[0] || '').trim();

    // Single-item legacy rows already scoped to the source page — rewrite detail only.
    if (items.length === 1 && item.direction === 'from' && sourceUrlId && keyUrlId && sourceUrlId !== keyUrlId) {
      const suffix =
        parseInternalLinkIssueKeySuffix(record.issueKey) ||
        internalLinkPairKey(sourceUrl, targetUrl, anchor);
      replacements.push({
        issueKey: buildPlanV2IssueKey(sourceUrlId, 'internal_link', suffix),
        urlId: sourceUrlId,
        url: sourceUrl || record.url,
        issueType: 'internal_link',
        suggestionKey: suffix,
        cardTitle: 'Internal link',
        changeSummary: `Link to ${targetPath} (“${truncate(anchor, 36)}”).`,
        why: 'Add a contextual link in existing copy.',
        priorityScore: record.priorityScore ?? null,
        actionDetail: encodeWorkItemDetail({
          internalLinkItems: [
            {
              direction: 'to',
              pagePath: targetPath,
              pageUrl: targetUrl,
              linkHref: targetUrl,
              anchor,
              score: item.score,
              sentence: item.sentence,
              placementParts: item.placementParts
            }
          ]
        })
      });
    }
  }

  const deduped = new Map<string, PlanV2IssueCardPayload>();
  for (const payload of replacements) {
    deduped.set(payload.issueKey, payload);
  }
  const unique = [...deduped.values()];
  if (!unique.length) return null;
  return { deleteIssueKey: record.issueKey, replacements: unique };
}
